import { z } from 'zod';

/**
 * Metadata for a response that always represents a student.
 *
 * @example
 * ```ts
 * const studentResponseSchema = z.object({ id: z.string(), name: z.string() })
 *   .extend(studentPiiType.shape);
 * type StudentResponse = z.infer<typeof studentResponseSchema>;
 *
 * function studentFromRaw(raw: { id: string; name: string; aliasId?: string }): StudentResponse {
 *   return { id: raw.id, name: raw.name, piiEntityType: 'student', studentAliasId: raw.aliasId };
 * }
 * ```
 */
export const studentPiiType = z.object({
  piiEntityType: z.literal('student'),
  studentAliasId: z.string().optional(),
});

export const coachPiiType = z.object({
  piiEntityType: z.literal('coach'),
});

export const studentContactPiiType = z.object({
  piiEntityType: z.literal('studentContact'),
});

// This constraint makes a mismatched registry key and discriminator a type error.
function definePiiTypeRegistry<const T extends Record<string, z.AnyZodObject>>(
  registry: T & { [K in keyof T]: T[K]['shape'] extends { piiEntityType: z.ZodLiteral<K & string> } ? unknown : never },
): T {
  return registry;
}

export const piiTypeRegistry = definePiiTypeRegistry({
  student: studentPiiType,
  coach: coachPiiType,
  studentContact: studentContactPiiType,
});

export type PiiEntityType = keyof typeof piiTypeRegistry;
type PiiSelection = readonly [PiiEntityType, ...PiiEntityType[]];
type UniqueSelection<T extends readonly PiiEntityType[], Seen = never> = T extends readonly [
  infer Head extends PiiEntityType,
  ...infer Tail extends PiiEntityType[],
]
  ? Head extends Seen
    ? never
    : UniqueSelection<Tail, Seen | Head>
  : unknown;
type SelectedSchemas<T extends PiiSelection> = {
  -readonly [K in keyof T]: T[K] extends PiiEntityType ? (typeof piiTypeRegistry)[T[K]] : never;
};
type SelectedPiiSchema<T extends PiiSelection> = T extends readonly [PiiEntityType]
  ? SelectedSchemas<T>[0]
  : T extends readonly [PiiEntityType, PiiEntityType, ...PiiEntityType[]]
    ? z.ZodDiscriminatedUnion<'piiEntityType', SelectedSchemas<T>>
    : never;

/**
 * Select complete metadata variants for composition with a business schema using `.and()`.
 * Use stripping object schemas: strict objects reject the other side's keys. Complete
 * object extensions before `.and()`, because the result is a Zod intersection.
 *
 * A mapper must populate the discriminator; defining a schema does not add it to
 * a response. Classify the raw data explicitly rather than assuming a default.
 *
 * @example
 * ```ts
 * const userResponseSchema = z.object({ id: z.string(), name: z.string() })
 *   .and(piiTypesOf(['student', 'coach']));
 * type UserResponse = z.infer<typeof userResponseSchema>;
 * type RawUser = { id: string; name: string } &
 *   ({ kind: 'student'; aliasId?: string } | { kind: 'coach' });
 *
 * function userFromRaw(raw: RawUser): UserResponse {
 *   if (raw.kind === 'student') {
 *     return { id: raw.id, name: raw.name, piiEntityType: 'student', studentAliasId: raw.aliasId };
 *   }
 *   return { id: raw.id, name: raw.name, piiEntityType: 'coach' };
 * }
 * ```
 */
export function piiTypesOf<const T extends PiiSelection>(types: T & UniqueSelection<T>): SelectedPiiSchema<T> {
  if (types.length === 0 || new Set(types).size !== types.length) {
    throw new Error('PII types must be nonempty and distinct');
  }
  const schemas = types.map((type) => {
    if (!Object.hasOwn(piiTypeRegistry, type)) {
      throw new Error(`Unknown PII type: ${type}`);
    }
    return piiTypeRegistry[type];
  });
  if (schemas.length === 1) {
    return schemas[0] as SelectedPiiSchema<T>;
  }
  return z.discriminatedUnion(
    'piiEntityType',
    schemas as [(typeof schemas)[number], (typeof schemas)[number], ...(typeof schemas)[number][]],
  ) as SelectedPiiSchema<T>;
}

const piiEntityTypes = Object.keys(piiTypeRegistry) as [PiiEntityType, ...PiiEntityType[]];

/** Validator derived from the registry's keys. */
export const piiEntityTypeSchema = z.enum(piiEntityTypes);

/** Select every registered PII variant. */
export function allPiiTypes() {
  return z.discriminatedUnion(
    'piiEntityType',
    Object.values(piiTypeRegistry) as [
      (typeof piiTypeRegistry)[PiiEntityType],
      (typeof piiTypeRegistry)[PiiEntityType],
      ...(typeof piiTypeRegistry)[PiiEntityType][],
    ],
  );
}
